import React from 'react';

interface TimezoneDistribution {
  timezone: string;
  patronCount: number;
  percentage: number;
  utcOffset: string;
}

interface TimezoneChartProps {
  data: TimezoneDistribution[];
  limit?: number;
}

/**
 * Bar chart showing patron distribution across timezones
 */
export const TimezoneChart: React.FC<TimezoneChartProps> = ({
  data,
  limit = 10,
}) => {
  // Sort by patron count and limit
  const sortedData = [...data]
    .sort((a, b) => b.patronCount - a.patronCount)
    .slice(0, limit);

  const maxCount = Math.max(...sortedData.map((d) => d.patronCount), 1);

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-8 text-center">
        <p className="text-gray-500">No timezone data available</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          Patron Timezone Distribution
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Top {sortedData.length} timezones by patron count
        </p>
      </div>

      <div className="space-y-4">
        {sortedData.map((tz, index) => (
          <div key={tz.timezone} className="group">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">
                  {tz.timezone === 'Unknown' ? 'Unknown' : tz.timezone}
                </span>
                <span className="text-xs text-gray-500">
                  {tz.utcOffset !== 'Unknown' && `(${tz.utcOffset})`}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600">
                  {tz.patronCount} patron{tz.patronCount !== 1 ? 's' : ''}
                </span>
                <span className="text-sm font-semibold text-gray-900 min-w-[3rem] text-right">
                  {tz.percentage.toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="relative w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-purple-500 to-blue-500 h-3 rounded-full transition-all duration-500 ease-out group-hover:from-purple-600 group-hover:to-blue-600"
                style={{
                  width: `${(tz.patronCount / maxCount) * 100}%`,
                }}
              >
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Best time to post hint */}
      {sortedData.length > 0 && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <p className="text-sm font-semibold text-blue-900">
                Optimize your posting time
              </p>
              <p className="text-xs text-blue-700 mt-1">
                Most of your patrons are in <strong>{sortedData[0].timezone}</strong>.
                Consider posting during peak hours in this timezone for maximum engagement.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimezoneChart;
